
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  User,
  Mail,
  Calendar,
  Edit
} from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

interface UserStatus {
  id: string;
  user_id: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  reason?: string;
  updated_at: string;
  updated_by: string;
}

interface StatusHistory {
  id: string;
  user_id: string;
  old_status: string;
  new_status: string;
  reason: string;
  changed_by: string;
  changed_at: string;
}

const AdvancedStatusManager: React.FC = () => {
  const { user, hasAccess } = useSupabaseAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hasAccess('admin')) {
      fetchUsers();
    }
  }, [hasAccess]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(profiles || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusHistory = async (userId: string) => {
    try {
      // Since the table doesn't exist, we'll use a mock implementation
      // In a real implementation, you would fetch from a status_history table
      const mockHistory: StatusHistory[] = [
        {
          id: '1',
          user_id: userId,
          old_status: 'pending',
          new_status: 'active',
          reason: 'Initial approval',
          changed_by: user?.id || '',
          changed_at: new Date().toISOString()
        }
      ];
      setStatusHistory(mockHistory);
    } catch (error) {
      console.error('Error fetching status history:', error);
    }
  };

  const updateUserStatus = async () => {
    if (!selectedUser || !newStatus || !reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Update the user's approval status
      const { error } = await supabase
        .from('profiles')
        .update({ is_approved: newStatus === 'active' })
        .eq('id', selectedUser.id);

      if (error) throw error;

      // Log the status change (mock implementation)
      console.log('Status updated:', {
        userId: selectedUser.id,
        oldStatus: selectedUser.is_approved ? 'active' : 'inactive',
        newStatus,
        reason,
        changedBy: user?.id
      });

      toast.success('User status updated successfully');
      setSelectedUser(null);
      setNewStatus('');
      setReason('');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  if (!hasAccess('admin')) {
    return (
      <Card>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You don't have permission to access this feature.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Advanced Status Manager</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Advanced Status Manager</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users.map((userProfile) => (
              <div key={userProfile.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span className="font-medium">{userProfile.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span className="text-sm text-muted-foreground">{userProfile.email}</span>
                  </div>
                  <Badge variant={userProfile.is_approved ? 'default' : 'secondary'}>
                    {userProfile.is_approved ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(userProfile);
                      fetchStatusHistory(userProfile.id);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedUser && (
        <Card>
          <CardHeader>
            <CardTitle>Update Status for {selectedUser.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Select status...</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Reason</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full p-2 border rounded-md"
                  rows={3}
                  placeholder="Enter reason for status change..."
                />
              </div>

              <div className="flex space-x-2">
                <Button onClick={updateUserStatus}>
                  Update Status
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSelectedUser(null);
                    setNewStatus('');
                    setReason('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>

            {statusHistory.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium mb-3">Status History</h4>
                <div className="space-y-2">
                  {statusHistory.map((history) => (
                    <div key={history.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-sm">
                          {history.old_status} → {history.new_status}
                        </span>
                        <p className="text-xs text-muted-foreground">{history.reason}</p>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(history.changed_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedStatusManager;
